/**
 * Which of the three fee models a Seller Collects surface is describing.
 * `unknownFee` is a real state, not a loading one: the premium comes from the
 * seller's plan and a signed-out viewer cannot resolve it.
 *
 * Lives beside the other Seller Collects helpers rather than inside the bid
 * modal, where it started: the banner, the order header, the sticky bar, the
 * first-win modal and the analytics helper all read it, and a helper that
 * imports from a component folder points the dependency the wrong way.
 */
export type FeeModel = 'feeBearing' | 'feeFree' | 'unknownFee'

/**
 * The fee model for a sale, given the seller's plan.
 *
 * `undefined` means "still resolving" and callers should wait; `unknownFee`
 * means it is knowably unresolvable, which is a renderable state.
 */
export const getFeeModel = ({
  isLoggedIn,
  isLoading,
  isError,
  buyersPremium,
}: {
  isLoggedIn: boolean
  isLoading: boolean
  isError: boolean
  buyersPremium: number | undefined
}): FeeModel | undefined => {
  if (!isLoggedIn) return 'unknownFee'
  if (isLoading || isError) return undefined
  return (buyersPremium ?? 0) > 0 ? 'feeBearing' : 'feeFree'
}

/**
 * Whether the storefront charges the buyer a fee, from its fee model.
 *
 * `undefined` for both unresolved and unresolvable: the two answers say
 * opposite things about money, so a surface must claim neither until it knows.
 * Shared so the bid and Buy Now modals cannot decide this differently for the
 * same lot — a failed plan lookup once read as fee-free on one and as nothing
 * at all on the other.
 */
export const feeModelHasBuyerPremium = (
  feeModel: FeeModel | undefined
): boolean | undefined =>
  feeModel === undefined || feeModel === 'unknownFee'
    ? undefined
    : feeModel === 'feeBearing'
